<script setup lang="ts">
import {computed, ref} from "vue";
import {Send} from "@lucide/vue";
import {Button} from "@/core/components/ui/button";
import {useMedicationCheckupStore} from "@/medication-checkup/stores/medication-checkup-store";
import MedicationCheckupCopiloteLine from "@/medication-checkup/components/MedicationCheckupCopiloteLine.vue";
import MedicationCheckupUserEcho from "@/medication-checkup/components/MedicationCheckupUserEcho.vue";
import MedicationCheckupCopiloteBubble from "@/medication-checkup/components/MedicationCheckupCopiloteBubble.vue";
import MedicationCheckupSceneReplies from "@/medication-checkup/components/MedicationCheckupSceneReplies.vue";
import MedicationCheckupBillingReminder from "@/medication-checkup/components/MedicationCheckupBillingReminder.vue";
import MSSanteDialog from "@/mssante/components/MSSanteDialog.vue";
import {MedicationCheckupAction} from "@/medication-checkup/interfaces";
import {AnalysisClosingView} from "@/medication-checkup/interfaces/analysis-closing";
import {DoctorTransmissionChannel, DoctorTransmissionView} from "@/medication-checkup/interfaces/doctor-transmission";
import {SceneProps, useScene} from "@/medication-checkup/composables/useScene";
import {CARD_SHEET} from "@/medication-checkup/services/SceneClasses";
import {useDoctorSynthesisMessage} from "@/medication-checkup/composables/useDoctorSynthesisMessage";

/**
 * The scene of the transmission of the analysis to the attending doctor:
 * the last thing the analysis does before the doctor answers.
 *
 * Its two replies are the two channels the handler serves. A direct exchange
 * is recorded on the click. MSSanté opens the dialog first, and the channel
 * is recorded once the message has gone out: the send is the dialog's, the
 * record the block's. Should the record fail after the send, the pharmacist
 * records it as a direct exchange rather than sending twice.
 *
 * The letter carries the conclusion signed at the closing of the analysis,
 * read from that block in the step shown. Once recorded, the transmission
 * reminds the act the analysis is billed as, and the synthesis can be sent
 * again; those sends are not recorded.
 */

const props = defineProps<SceneProps<DoctorTransmissionView>>();

const {bubbleShown, cardShown: repliesShown, clicked, view, text, actions, closing, acted, act} = useScene(
    props,
    {card: true}
);

const store = useMedicationCheckupStore();

const MSSANTE: DoctorTransmissionChannel = 'mssante';

/**
 * The conclusion of the analysis, read from its closing block.
 */
const conclusion = computed(() => {
    const block = store.displayedBlocks.find(candidate => candidate.definition_key === 'analysis_closing');

    return (block?.view as AnalysisClosingView | null | undefined)?.conclusion ?? null;
});

const {customer, open: mssanteOpen, prefills} = useDoctorSynthesisMessage(
    () => view.value?.doctor_synthesis,
    () => conclusion.value,
    () => view.value?.sender
);

/**
 * The MSSanté reply, held while its dialog is open: it is taken once the
 * message has gone out, never on the click.
 */
const sending = ref<MedicationCheckupAction | null>(null);

const reply = (action: MedicationCheckupAction) => {
    if (action.key !== MSSANTE) {
        act(action);

        return;
    }

    sending.value = action;
    mssanteOpen.value = true;
};

/**
 * The dialog sent the message: record the transmission, unless the block is
 * already completed — a send again from the frozen scene.
 */
const sent = () => {
    const action = sending.value;

    sending.value = null;

    if (!props.frozen && action) {
        act(action);
    }
};
</script>

<template>
  <div class="flex flex-col gap-3.5">
    <template v-if="frozen">
      <MedicationCheckupCopiloteLine v-if="text" :text="text" />
      <MedicationCheckupUserEcho v-if="acted" :text="acted" />
      <MedicationCheckupCopiloteLine v-if="closing" :text="closing" />
      <div
        v-if="view?.billing?.length"
        :class="CARD_SHEET"
        class="max-w-[640px] rounded-xl overflow-hidden"
      >
        <MedicationCheckupBillingReminder class="bg-white" :lines="view.billing" />
      </div>
      <Button
        v-if="view?.doctor_synthesis"
        variant="outline"
        class="self-start ml-10 inline-flex items-center gap-1.5 text-sm"
        @click="mssanteOpen = true"
      >
        <Send class="w-3.5 h-3.5" />
        Renvoyer la synthèse par MSSanté
      </Button>
    </template>

    <template v-else>
      <MedicationCheckupCopiloteBubble
        :shown="bubbleShown"
        :text="text"
      />

      <MedicationCheckupSceneReplies
        :clicked="clicked"
        :actions="actions"
        :shown="repliesShown"
        @act="reply"
      />
    </template>

    <MSSanteDialog
      v-if="view?.doctor_synthesis"
      v-model:open="mssanteOpen"
      :prefills="prefills"
      :consumer-key="customer?.consumer_key"
      :patient-name="customer?.information?.name"
      :source="store.cycle ? {type: 'medication-checkup', id: store.cycle.checkup_id} : undefined"
      @sent="sent"
    />
  </div>
</template>
